/**
 * Locale formatting. The Server never formats money, dates or percentages —
 * it sends integer minor units plus an ISO 4217 code and clients render them.
 */
import type { Money } from '@/generated/types'

const locale = typeof navigator !== 'undefined' ? navigator.language : 'en-US'

/**
 * The locale everything on this surface is rendered in. Exported because
 * generated text carries the locale it was WRITTEN in and has to be compared
 * against the one being read in — see lib/generatedText.ts localeMatch.
 */
export const displayLocale = locale

const currencyFormatters = new Map<string, Intl.NumberFormat>()
const fractionDigits = new Map<string, number>()

function currencyFormatter(currency: string, opts: Intl.NumberFormatOptions = {}): Intl.NumberFormat {
  const key = `${currency}:${JSON.stringify(opts)}`
  let f = currencyFormatters.get(key)
  if (!f) {
    f = new Intl.NumberFormat(locale, { style: 'currency', currency, ...opts })
    currencyFormatters.set(key, f)
  }
  return f
}

/** Minor units per major unit for a currency — 100 for USD, 1 for JPY. */
function minorUnitScale(currency: string): number {
  let digits = fractionDigits.get(currency)
  if (digits === undefined) {
    digits = currencyFormatter(currency).resolvedOptions().maximumFractionDigits ?? 2
    fractionDigits.set(currency, digits)
  }
  return 10 ** digits
}

export function toMajorUnits(money: Money): number {
  return money.amount / minorUnitScale(money.currency)
}

export interface MoneyFormatOptions {
  /** Drop the sign and show the magnitude — for "Spent", chart labels, totals. */
  absolute?: boolean
  /** Force a leading + on positive values. */
  signed?: boolean
  /** Round to whole major units. Used in dense charts and axis labels. */
  compact?: boolean
}

export function formatMoney(money: Money | null | undefined, opts: MoneyFormatOptions = {}): string {
  if (!money) return '—'
  const scale = minorUnitScale(money.currency)
  let value = money.amount / scale
  if (opts.absolute) value = Math.abs(value)
  const fmt = currencyFormatter(money.currency, {
    ...(opts.compact ? { maximumFractionDigits: 0, notation: 'compact' as const } : {}),
    ...(opts.signed && !opts.absolute ? { signDisplay: 'exceptZero' as const } : {}),
  })
  return fmt.format(value)
}

/** A money value that reads as zero regardless of currency minor units. */
export function zeroMoney(currency: string): Money {
  return { amount: 0, currency }
}

export function addMoney(a: Money, b: Money): Money {
  return { amount: a.amount + b.amount, currency: a.currency }
}

export function sumMoney(items: (Money | undefined)[], currency: string): Money {
  return items.reduce<Money>(
    (acc, m) => (m ? { amount: acc.amount + m.amount, currency: acc.currency } : acc),
    zeroMoney(currency),
  )
}

const dateFormatter = new Intl.DateTimeFormat(locale, { year: 'numeric', month: 'short', day: 'numeric' })
const dayMonthFormatter = new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' })
const monthFormatter = new Intl.DateTimeFormat(locale, { year: 'numeric', month: 'long' })
const monthShortFormatter = new Intl.DateTimeFormat(locale, { month: 'short' })

/** Parse a YYYY-MM-DD date as local time — `new Date(iso)` would shift it by the UTC offset. */
export function parseDate(iso: string): Date {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number)
  return new Date(y, (m ?? 1) - 1, d ?? 1)
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  return dateFormatter.format(parseDate(iso))
}

export function formatDayMonth(iso: string | null | undefined): string {
  if (!iso) return '—'
  return dayMonthFormatter.format(parseDate(iso))
}

/** `2026-07` -> "July 2026". */
export function formatMonth(month: string): string {
  return monthFormatter.format(parseDate(`${month}-01`))
}

/** `2026-07` -> "Jul". Axis labels and dense rows. */
export function formatMonthShort(month: string): string {
  return monthShortFormatter.format(parseDate(`${month}-01`))
}

export function formatPercent(ratio: number | null | undefined, digits = 0): string {
  if (ratio === null || ratio === undefined || Number.isNaN(ratio)) return '—'
  return new Intl.NumberFormat(locale, {
    style: 'percent',
    maximumFractionDigits: digits,
    minimumFractionDigits: digits,
  }).format(ratio)
}

/** 2499 basis points -> "24.99%". */
export function formatBasisPoints(bp: number | null | undefined): string {
  if (bp === null || bp === undefined) return '—'
  return new Intl.NumberFormat(locale, {
    style: 'percent',
    maximumFractionDigits: 2,
  }).format(bp / 10000)
}

export function formatNumber(n: number, digits = 0): string {
  return new Intl.NumberFormat(locale, { maximumFractionDigits: digits }).format(n)
}

export function formatFileSize(bytes: number): string {
  const mb = bytes / (1024 * 1024)
  if (mb >= 1) return `${formatNumber(mb, mb < 10 ? 1 : 0)} MB`
  return `${formatNumber(bytes / 1024, 0)} KB`
}

/** Current month as `YYYY-MM`, used for default selections. */
export function currentMonthKey(now = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
}

export function monthKeyOf(iso: string): string {
  return iso.slice(0, 7)
}

export function todayIso(now = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
}
